import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { CloseShiftReceipt } from '@smile/shared';
import { ApiError, api } from '../api/client';
import { can } from '../lib/perms';
import { longDt } from '../lib/filters';

/**
 * "Uzavřít směnu" — the legacy dashboard's close-shift form under the till:
 * a note, the roster (checkboxes, the person pressing pre-ticked), the
 * button, and the legacy's "Opravdu chcete uzavřít směnu?" as a second
 * press rather than a modal. On success the receipt says which window was
 * closed, and the Směny screen has the new row.
 */
export function CloseShiftForm() {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [confirm, setConfirm] = useState(false);
  const [note, setNote] = useState('');
  const [picked, setPicked] = useState<Set<number>>(new Set());
  const [receipt, setReceipt] = useState<CloseShiftReceipt | null>(null);

  const me = useQuery({ queryKey: ['me'], queryFn: () => api.me(), staleTime: 10 * 60_000 });
  const staff = useQuery({ queryKey: ['catalog', 'staff'], queryFn: () => api.staff(), staleTime: 10 * 60_000, enabled: open });
  const canWrite = can(me.data, '/balances/*', 'write');

  const m = useMutation({
    mutationFn: () => api.closeShift({ note: note.trim(), userIds: [...picked] }),
    onSuccess: async (rc) => {
      setReceipt(rc);
      setOpen(false);
      setConfirm(false);
      await qc.invalidateQueries();
    },
    onError: () => setConfirm(false),
  });

  if (!canWrite) return null;

  if (receipt) {
    return (
      <div className="closeshift done" role="status">
        ✔ Směna uzavřena: {longDt(receipt.started)} – {longDt(receipt.closed)} · {receipt.userIds.length}{' '}
        {receipt.userIds.length === 1 ? 'osoba' : 'osoby'}.{' '}
        {/* The printed uzávěrka carries GoPay and invoices: the admins' (30 Oct 2026). */}
        {can(me.data, '/balances/*', 'read') ? (
          <a className="btn sm" href={`/uzaverka/${receipt.id}`} target="_blank" rel="noopener">
            Tisk uzávěrky
          </a>
        ) : null}
      </div>
    );
  }

  const err = m.error;
  const message =
    err instanceof ApiError
      ? err.serverMessage ?? (err.code === 'forbidden' ? 'Na tuhle akci nemáte oprávnění.' : err.message)
      : err instanceof Error
        ? err.message
        : null;

  if (!open) {
    return (
      <div className="closeshift">
        <button type="button" className="btn primary block" onClick={() => setOpen(true)}>
          Uzavřít směnu
        </button>
      </div>
    );
  }

  const toggle = (id: number) => {
    const next = new Set(picked);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setPicked(next);
  };

  return (
    <form
      className="closeshift form"
      onSubmit={(e) => {
        e.preventDefault();
        if (m.isPending) return;
        if (!confirm) setConfirm(true);
        else m.mutate();
      }}
    >
      <label className="ef wide">
        Poznámka k uzávěrce směny
        <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2} maxLength={2000} autoFocus />
      </label>
      <div className="roster">
        <span className="lbl">Na směně</span>
        {(staff.data ?? []).map((u) => {
          const isMe = me.data?.id === u.id;
          return (
            <label key={u.id} className="rosterbox">
              <input type="checkbox" checked={isMe || picked.has(u.id)} disabled={isMe} onChange={() => toggle(u.id)} />{' '}
              {u.name}
            </label>
          );
        })}
      </div>
      <div className="efactions">
        <button type="submit" className={confirm ? 'btn warn' : 'btn primary'} disabled={m.isPending}>
          {m.isPending ? 'Uzavírám…' : confirm ? 'Opravdu uzavřít směnu' : 'Uzavřít směnu'}
        </button>
        <button
          type="button"
          className="btn"
          onClick={() => {
            setOpen(false);
            setConfirm(false);
          }}
          disabled={m.isPending}
        >
          Zrušit
        </button>
        {message && (
          <span className="arrive-error" role="alert">
            {message}
          </span>
        )}
      </div>
    </form>
  );
}
